import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import './Toast.css';

const Toast = ({ message, type, duration, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Match this with fade-out duration
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg className="toast-icon" viewBox="0 0 24 24">
            <path fill="currentColor" d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" />
          </svg>
        );
      case 'error':
        return (
          <svg className="toast-icon" viewBox="0 0 24 24">
            <path fill="currentColor" d="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="toast-icon" viewBox="0 0 24 24">
            <path fill="currentColor" d="M12 2L1 21H23M12 6L19.5 19H4.5M11 10V14H13V10M11 16V18H13V16" />
          </svg>
        );
      default:
        return (
          <svg className="toast-icon" viewBox="0 0 24 24">
            <path fill="currentColor" d="M13 13H11V7H13M13 17H11V15H13M12 2A10 10 0 0 0 2 12A10 10 0 0 0 12 22A10 10 0 0 0 22 12A10 10 0 0 0 12 2Z" />
          </svg>
        );
    }
  };

  return (
    <div className={`toast ${type} ${isVisible ? 'slide-in' : 'fade-out'}`}>
      <div className="toast-content">
        {getIcon()}
        <span className="toast-message">{message}</span>
      </div>
      <div className="toast-progress" style={{ animationDuration: `${duration}ms` }}></div>
    </div>
  );
};

const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const addToast = (message, type, duration = 3000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type, duration }]);
  };

  // Listen for toast events
  useEffect(() => {
    const handleToastEvent = (e) => {
      addToast(e.detail.message, e.detail.type, e.detail.duration);
    };

    document.addEventListener('addToast', handleToastEvent);
    return () => document.removeEventListener('addToast', handleToastEvent);
  }, []);

  return (
    <div className="toast-container">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

// Initialize toast container automatically
const initToastContainer = () => {
  let toastRoot = document.getElementById('toast-root');

  if (!toastRoot) {
    toastRoot = document.createElement('div');
    toastRoot.id = 'toast-root';
    document.body.appendChild(toastRoot);

    const root = createRoot(toastRoot);
    root.render(<ToastContainer />);
  }
};

// Initialize when imported
initToastContainer();

// Public API
const toast = {
  success: (message, duration = 3000) => {
    document.dispatchEvent(new CustomEvent('addToast', {
      detail: { message, type: 'success', duration }
    }));
  },
  error: (message, duration = 3000) => {
    document.dispatchEvent(new CustomEvent('addToast', {
      detail: { message, type: 'error', duration }
    }));
  },
  warning: (message, duration = 3000) => {
    document.dispatchEvent(new CustomEvent('addToast', {
      detail: { message, type: 'warning', duration }
    }));
  },
  info: (message, duration = 3000) => {
    document.dispatchEvent(new CustomEvent('addToast', {
      detail: { message, type: 'info', duration }
    }));
  }
};

export default toast;